/**
 * @file Submission read-only / review UI components — DatasetOverviewFormShell.tsx.
 */

import React from "react";
import "./DatasetOverviewFormShell.css";
import type { DatasetOverviewFormVariant } from "./types";

const ROOT_CLASS: Record<DatasetOverviewFormVariant, string> = {
  contributor: "contributor-details-readonly contributor-form-container",
  publication: "publication-details-readonly publication-form-container",
  background: "background-details-readonly background-form-container",
  "lifecycle-a1": "a1-details-readonly formA1-container",
  "lifecycle-a2": "a2-details-readonly formA2-container",
  "lifecycle-a3": "a3-details-readonly formA3-container",
  a1a3: "a1a3-details-readonly a1a3-container",
};

const FIELDS_CLASS: Partial<Record<DatasetOverviewFormVariant, string>> = {
  contributor: "contributor-details-readonly__fields",
  publication: "publication-details-readonly__fields",
  background: "background-details-readonly__fields form-content form-content-background",
};

/** Type definition — Dataset Overview Form Shell Props. */
export type DatasetOverviewFormShellProps = {
  variant: DatasetOverviewFormVariant;
  children?: React.ReactNode;
  /** When set, renders empty note instead of children. */
  emptyMessage?: string;
  /** Override inner fields wrapper class (e.g. lifecycle uses direct children). */
  fieldsClassName?: string;
  /** Skip inner fields wrapper — children render directly in the root. */
  bare?: boolean;
  className?: string;
};

/** Shared read-only form container for Dataset Overview sections. */
const DatasetOverviewFormShell: React.FC<DatasetOverviewFormShellProps> = ({
  variant,
  children,
  emptyMessage,
  fieldsClassName,
  bare = false,
  className = "",
}) => {
  const rootClass = `${ROOT_CLASS[variant]} ${className}`.trim();

  if (emptyMessage) {
    return (
      <div className={`${rootClass} empty-data-note`}>{emptyMessage}</div>
    );
  }

  if (bare) {
    return <div className={rootClass}>{children}</div>;
  }

  const innerClass =
    fieldsClassName ?? FIELDS_CLASS[variant] ?? `${variant}-readonly__fields`;

  return (
    <div className={rootClass}>
      <div className={innerClass}>{children}</div>
    </div>
  );
};

/** React component — Dataset Overview Form Shell. */
export default DatasetOverviewFormShell;
